import {Box} from '@mui/material';
import {t} from 'i18next';
import AurSvgIcon from '../../common/aur-svg-icon/AurSvgIcon';
import {useEffect, useLayoutEffect, useMemo, useRef} from 'react';
import {
  GridColDef,
  GridFilterModel,
  GridRenderCellParams,
  GridRenderHeaderFilterProps,
  GridRowParams,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import AurDataGridPro, { CustomGridColDef } from '../../common/aur-data-grid-pro/AurDataGridPro';
import AurPagination from '../../common/aur-pagination/AurPagination';
import {TABLE_PAGE_SIZE_OPTION} from '../../../constants/constants';
import {SearchRowType} from './EcaSearchTermReports';
import AurIcon, {IconColors, IconNames} from '../../common/aur-icon/AurIcon';
import {getUTCDateTimeForDisplay} from '../../../utils/utils';
import FieldPopoverGridFilter from '../../pages/field-management/field-table/FieldPopoverGridFilter';
import AurInfographicSeperator from '../../common/aur-infographic/AurInfographicSeperator';
import AurButton from '../../common/aur-button/AurButton';
import {
  getParseSTRStatus,
  getSTRStatusString,
  STRStatusType,
  useSTRReportStatusList,
} from '../../pages/view-str/hook/useSTRStatus';
import AurDataGridNoRowsOverlay from '../../common/aur-data-grid-no-rows-overlay/AurDataGridNoRowsOverlay';
import AurDataGridLoadingOverlay from '../../common/aur-data-grid-loading-overlay/AurDataGridLoadingOverlay';
import AurDataGridErrorOverlay from '../../common/aur-data-grid-error-overlay/AurDataGridErrorOverlay';
import FilterTextField from '../../common/aur-data-grid-pro/FilterTextField';
import {useBlockElementScroll} from '../../pages/view-str/hook/useBlockElementScroll';

const TABLE_BASE_WIDTH = 1328;

interface SearchTermTableProp {
  data: SearchRowType[];
  totalItems: number; // Total number of items to paginate through
  itemsPerPage: number; // Number of items per page
  currentPage: number;
  onPageChange: (page: number) => void; // Function to handle page change
  setItemsPerPage: (itemsPerPage: number) => void;
  isReqLoading: boolean;
  haveReqError: boolean;
  onErrorRefresh: () => void;
  handleRowClick: (params: GridRowParams) => void; // to handle row click
  onSetSortModel: (sortModel: GridSortModel) => void;
  sortModel: GridSortModel;
  handleOnFilterModelChange: (model: GridFilterModel) => void;
  filterCount: number;
}
const ECASearchTermReportTable: React.FC<SearchTermTableProp> = ({
  data,
  totalItems,
  itemsPerPage,
  currentPage,
  onPageChange,
  setItemsPerPage,
  isReqLoading,
  haveReqError,
  onErrorRefresh,
  handleRowClick,
  onSetSortModel,
  sortModel,
  handleOnFilterModelChange,
  filterCount,
}) => {
  const stringPath = 'eca.searchTermReport.';
  const strReportStatusList = useSTRReportStatusList();

  const renderDateTimeCell = (params: GridRenderCellParams) => {
    const {value} = params;
    return (
      <span className="eca-str-cell-ellipsis">
        {getUTCDateTimeForDisplay(value)}
      </span>
    );
  };
  const apiRef = useGridApiRef();

  const renderHeaderFilter = (
    params: GridRenderHeaderFilterProps,
    dataTestId?: string
  ) => {
    return (
      <FilterTextField
        gridRenderHeaderFilterProps={params}
        placeholder={t('fieldManagement.fieldTable.search')}
        parentClass="filter-input-field-container"
        data-testid={dataTestId}
      />
    );
  };

  const columns: CustomGridColDef<SearchRowType[][number]>[] = useMemo(() => {
    return [
      {
        field: 'id',
        headerName: '#',
        flex: 48 / TABLE_BASE_WIDTH,
        filterable: false,
        sortable: false,
        resizable: false,
        renderHeaderFilter: () => (
          <span className="sr-only" tabIndex={-1}>
            {t('common.gridColHeaderFilter')}
          </span>
        ),
      },
      {
        field: 'name',
        headerName: t(stringPath + 'table.name'),
        flex: 243 / TABLE_BASE_WIDTH,
        sortable: true,
        resizable: false,
        renderHeaderFilter: (param: GridRenderHeaderFilterProps) =>
          renderHeaderFilter(param, 'str-report-name-input-filter'),
      },
      {
        field: 'indexName',
        headerName: t(stringPath + 'table.indexName'),
        flex: 161 / TABLE_BASE_WIDTH,
        sortable: true,
        resizable: false,
        renderHeaderFilter: (param: GridRenderHeaderFilterProps) =>
          renderHeaderFilter(param, 'str-report-index-name-input-filter'),
      },
      {
        field: 'createdBy',
        headerName: t(stringPath + 'table.createdBy'),
        flex: 277 / TABLE_BASE_WIDTH,
        sortable: true,
        resizable: false,
        renderHeaderFilter: (param: GridRenderHeaderFilterProps) =>
          renderHeaderFilter(param, 'str-report-created-by-input-filter'),
      },
      {
        field: 'status',
        headerName: t(stringPath + 'table.status'),
        flex: 169 / TABLE_BASE_WIDTH,
        sortable: false,
        resizable: false,
        maxWidth: 169,
        renderCell: (params) => {
          const {value} = params;
          return (
            <div className="status-main-div">
              <span className="eca-str-cell-ellipsis">
                {t(getSTRStatusString(value))}
              </span>
              {getParseSTRStatus(value) === STRStatusType.Ready && (
                <span aria-hidden="true">
                  <AurIcon
                    icon={IconNames.CachedIcon}
                    color={IconColors.Custom}
                    customClassName="ready-status-icon"
                  />
                </span>
              )}
            </div>
          );
        },
        renderHeaderFilter: () => (
          <FieldPopoverGridFilter
            key={'status'}
            apiRef={apiRef}
            columnField={'status'}
            type={'options'}
            dataArray={strReportStatusList}
            inputClass="filter-input-field-container"
            inputType="text"
            caretIconContainerClass="caret-dropdown-icon"
            dataTestId="report-status-filter"
            inputTabIndex={0}
          />
        ),
      },
      {
        field: 'searchPopulation',
        headerName: t(stringPath + 'table.searchPopulation'),
        flex: 209 / TABLE_BASE_WIDTH,
        sortable: false,
        resizable: false,
        renderCell: (params) => {
          const isSearchIdEmpty = params.row.searchPopulation.name === '';
          return (
            <span className="eca-str-cell-ellipsis">
              {isSearchIdEmpty
                ? t(stringPath + 'table.allDocuments')
                : params.row.searchPopulation.name}
            </span>
          );
        },
        renderHeaderFilter: () => (
          <span className="sr-only">{t('common.gridColHeaderFilter')}</span>
        ),
      },
      {
        field: 'lastModifiedDate',
        headerName: t(stringPath + 'table.lastModifiedDate'),
        flex: 221 / TABLE_BASE_WIDTH,
        sortable: true,
        resizable: false,
        headerClassName: 'last-modified-header',
        renderCell: renderDateTimeCell,
        isDateColumn: true,
        renderHeaderFilter: () => (
          <FieldPopoverGridFilter
            apiRef={apiRef}
            columnField={'lastModifiedDate'}
            type={'date'}
            inputClass="filter-input-field-container"
            inputType="text"
            caretIconContainerClass="caret-dropdown-icon"
            dataTestId="last-modified-date-filter"
            disablePortal={true}
          />
        ),
      },
      {
        field: 'arrow',
        headerName: '',
        flex: 48 / TABLE_BASE_WIDTH,
        sortable: false,
        resizable: false,
        filterable: false,
        headerClassName: 'chevron-header',
        disableTooltip: true,
        renderCell: () => {
          return (
            <div className="right-icon" aria-hidden="true">
              <AurSvgIcon src={'images/chev_right.svg'} />
            </div>
          );
        },
        renderHeader: () => (
          <span className="sr-only">{t('common.actions')}</span>
        ),
        renderHeaderFilter: () => (
          <span className="sr-only">{t('common.gridColHeaderFilter')}</span>
        ),
      },
    ];
  }, [t, open]);

  const renderPaginationBlock = (ariaLabel: string) => {
    return (
      <Box className={'table-pagination-container'}>
        <Box className={'top-pagination'}>
          <AurPagination
            paginationAriaLabel={ariaLabel}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            perPageOptions={TABLE_PAGE_SIZE_OPTION}
            itemType={t('common.reports')}
            setItemsPerPage={setItemsPerPage}
            onPageChange={onPageChange}
            pageItemDropDownItemColor="#FCFCFD"
          />
        </Box>
        {filterCount > 0 ? (
          <>
            <Box>
              <AurInfographicSeperator seperatorIcon={'VerticalLine'} />
            </Box>
            <Box
              className={'apply-filter-button-container'}
              onClick={() => {
                apiRef.current?.setFilterModel({items: []});
              }}
            >
              <AurButton
                type={'exit'}
                size={'Small'}
                startIcon={<AurIcon icon={IconNames.ClearFiltersIcon} />}
                label={`${t('fieldManagement.fieldTable.clearFilters')} (${filterCount})`}
              />
            </Box>
          </>
        ) : null}
      </Box>
    );
  };
  const noDataGridOverlay = useMemo(() => {
    if (isReqLoading) return AurDataGridLoadingOverlay;
    if (haveReqError) return AurDataGridErrorOverlay;
    if (filterCount > 0 && data.length <= 0) return AurDataGridNoRowsOverlay;
    return AurDataGridNoRowsOverlay;
  }, [isReqLoading, haveReqError]) as React.JSXElementConstructor<unknown>;
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useBlockElementScroll(scrollRef);

  useEffect(() => {
    const wrapper = document.querySelector<HTMLElement>('.osano-cm-window');
    wrapper?.setAttribute('role', 'region');
    wrapper?.setAttribute('aria-label', t('common.footer.cookiePreferences'));
  }, []);

  useLayoutEffect(() => {
    const gridContainer = scrollRef.current;

    if (!gridContainer) {
      return;
    }

    const removeColumnHeadersFromTabOrder = () => {
      const columnHeaders = gridContainer.querySelectorAll<HTMLElement>(
        '.MuiDataGrid-columnHeader[role="columnheader"][tabindex="0"]'
      );

      columnHeaders.forEach((columnHeader) => {
        columnHeader.setAttribute('tabindex', '-1');
      });
    };

    // Apply immediately after the grid is rendered.

    removeColumnHeadersFromTabOrder();

    // MUI may restore tabindex="0" during sorting, filtering,

    // pagination, virtualization or focus changes.

    const observer = new MutationObserver(() => {
      removeColumnHeadersFromTabOrder();
    });

    observer.observe(gridContainer, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['tabindex'],
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div className="eca-searchterm-report-table">
      <div className="data-table-container" ref={scrollRef}>
        {renderPaginationBlock(t('common.topPagination'))}
        <AurDataGridPro
          className="aur-grid-pro"
          rows={data}
          columns={columns}
          hideFooter
          headerFilters
          disableRowSelectionOnClick
          enableServerSide={true}
          visibleToolTip
          noRowOverlayClass="no-row-container"
          onFilterModelChange={handleOnFilterModelChange}
          haveReqError={haveReqError}
          onErrorRefresh={onErrorRefresh}
          data-testid="str-table"
          checkBoxVisible={false}
          defaultItemsPerPage={itemsPerPage}
          columnHeaderHeight={48}
          headerFilterHeight={50}
          rowHeight={36}
          onRowClick={handleRowClick}
          sortModel={sortModel}
          setSortModel={onSetSortModel}
          apiRef={apiRef}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          disableCellHeaderFocus={true}
          slots={{
            headerFilterMenu: null,
            noRowsOverlay: noDataGridOverlay,
          }}
          slotProps={{
            noRowsOverlay: {
              onRefresh: onErrorRefresh,
              enableParentWrapper: false,
            } as unknown as Record<string, unknown>,
          }}
        />
        {renderPaginationBlock(t('common.bottomPagination'))}
      </div>
    </div>
  );
};

export default ECASearchTermReportTable;
